/**
 * Lab Results API - Lab tests and results management
 */
import { apiClient } from '../api-client';

// ==================== Types ====================

export interface LabResult {
  id: number;
  patient_id: number;
  test_name: string;
  test_type: string;
  test_date: string;
  result_value?: string;
  result_unit?: string;
  reference_range?: string;
  status: string;
  abnormal_flag?: boolean;
  notes?: string;
  ordered_by_id?: number;
  lab_name?: string;
  file_url?: string;
  created_at: string;
}

export interface LabResultCreate {
  patient_id: number;
  test_name: string;
  test_type: string;
  test_date: string;
  result_value?: string;
  result_unit?: string;
  reference_range?: string;
  notes?: string;
  lab_name?: string;
}

export interface LabReportUpload {
  patient_id: number;
  test_type: string;
  test_date: string;
  file: File;
  notes?: string;
}

// ==================== API Functions ====================

/**
 * Get lab results for current patient user
 */
export async function getMyLabResults(
  startDate?: string,
  endDate?: string
): Promise<LabResult[]> {
  const response = await apiClient.get(`/lab-results/me`, { start_date: startDate, end_date: endDate });
  return response;
}

/**
 * Upload lab report for current patient user
 */
export async function uploadMyLabReport(
  data: LabReportUpload
): Promise<LabResult> {
  const response = await apiClient.upload(
    `/lab-results/me/upload`,
    data.file,
    {
      test_type: data.test_type,
      test_date: data.test_date,
      notes: data.notes
    }
  );
  return response;
}

/**
 * Get lab results for a patient (with patient ID)
 */
export async function getLabResults(
  patientId: number,
  startDate?: string,
  endDate?: string
): Promise<LabResult[]> {
  const response = await apiClient.get(`/lab-results/${patientId}`, { start_date: startDate, end_date: endDate });
  return response;
}

/**
 * Get a specific lab result
 */
export async function getLabResult(id: number): Promise<LabResult> {
  const response = await apiClient.get(`/lab-results/result/${id}`);
  return response;
}

/**
 * Create lab result
 */
export async function createLabResult(
  data: LabResultCreate
): Promise<LabResult> {
  const response = await apiClient.post('/lab-results', data);
  return response;
}

/**
 * Upload lab report file (with patient ID in URL)
 */
export async function uploadLabReport(
  data: LabReportUpload
): Promise<LabResult> {
  const response = await apiClient.upload(
    `/lab-results/${data.patient_id}/upload`,
    data.file,
    {
      test_type: data.test_type,
      test_date: data.test_date,
      notes: data.notes
    }
  );
  return response;
}

/**
 * Delete lab result
 */
export async function deleteLabResult(id: number): Promise<{ status: string; message: string }> {
  const response = await apiClient.delete(`/lab-results/result/${id}`);
  return response;
}

// Export object for consistency
export const labResultsApi = {
  getMyLabResults,
  uploadMyLabReport,
  getLabResults,
  getLabResult,
  createLabResult,
  uploadLabReport,
  deleteLabResult,
};